import {SubmitHandler} from 'react-hook-form';
import {ContactCustomFieldId} from '../../../pages/HelpCenter/contactCustomFieldDefinitions';
import {HelpCenterEndpoint} from '../../../pages/HelpCenter/helpCenterEndpoint';
import {CustomField} from '../../../pages/HelpCenter/types';
import {orderNumberRegex} from '../../../utils/orderNumber';
import {FormDataRaw} from './types';
import {
  buildZendeskTicketComment,
  buildZendeskTicketSubject,
} from './zendeskHelper';

const createContactFormSubmitHandler =
  ({
    isVip,
    attachCountryOfShipping = false,
    sendContactForm,
    resetFormState,
    setFormPhase,
    localizedServiceEmailAddress,
    trackFormSubmit,
    onSuccess,
    onError,
    captchaCode,
    onSubmit,
  }: {
    isVip?: boolean;
    attachCountryOfShipping?: boolean;
    sendContactForm: HelpCenterEndpoint['sendContactForm'];
    resetFormState: () => void;
    setFormPhase: (phase: 'INPUT' | 'SUBMITTING' | 'SUCCESS' | 'ERROR') => void;
    localizedServiceEmailAddress: string;
    trackFormSubmit: VoidFunction;
    onSuccess: VoidFunction;
    onError: VoidFunction;
    captchaCode?: string;
    onSubmit?: VoidFunction;
  }): SubmitHandler<FormDataRaw> =>
  async values => {
    setFormPhase('SUBMITTING');
    onSubmit?.();

    const customFields: CustomField[] = [
      {
        id: ContactCustomFieldId.VIP,
        value: isVip ? 'VIP' : '-',
      },
    ];

    if (attachCountryOfShipping) {
      customFields.push({
        id: ContactCustomFieldId.CustomShippingCountry,
        value: values.country.value,
      });
    }

    const orderNumbers = (values.orderNumber ?? [])
      .map(({value}) => value)
      .filter(Boolean);

    if (values.isOrder === 'true') {
      const invoiceAddress = `${values.street} ${values.houseNumber}
${values.addressAdditional}
${values.zipCode} ${values.city}`;

      customFields.push({
        id: ContactCustomFieldId.InvoiceAddress,
        value: invoiceAddress,
      });

      customFields.push({
        id: ContactCustomFieldId.BirthDate,
        value: values.birthDate,
      });

      if (orderNumbers.length > 0) {
        const orderNumberString = orderNumbers[0];
        const [, orderNumberMatch] =
          orderNumberRegex.exec(orderNumberString) ?? [];
        if (orderNumberMatch) {
          customFields.push({
            id: ContactCustomFieldId.OrderNumber,
            value: parseInt(orderNumberMatch, 10),
          });
        }
      }
    }

    const subject = buildZendeskTicketSubject(values, orderNumbers);
    const comment = buildZendeskTicketComment(values, orderNumbers);

    try {
      await sendContactForm(
        {
          attachments: (values.attachments ?? []) as File[],
          captchaToken: captchaCode,
          subject,
          comment,
          email: values.email,
          customFields,
        },
        localizedServiceEmailAddress,
      );
      resetFormState();
      setFormPhase('SUCCESS');
      trackFormSubmit();
      onSuccess();
    } catch (e) {
      onError();
      setFormPhase('ERROR');
    }
  };

export default createContactFormSubmitHandler;
